import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { doc, getDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Dumbbell, Shuffle, Play, X } from 'lucide-react';
import { getInProgressWorkouts, deleteInProgressWorkout } from '../services/logService';
import toast from 'react-hot-toast';

const ADHOC_OPTIONS = [
  { focus: 'full-body', label: 'Full Body', emoji: '💪' },
  { focus: 'push', label: 'Push Day', emoji: '🫸' },
  { focus: 'pull', label: 'Pull Day', emoji: '🫷' },
  { focus: 'legs', label: 'Legs Day', emoji: '🦵' },
  { focus: 'glutes', label: 'Glutes', emoji: '🍑' },
  { focus: 'arms', label: 'Arms', emoji: '💪' },
];

function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tier, setTier] = useState(2);
  const [durationMin, setDurationMin] = useState(30);
  const [inProgressWorkout, setInProgressWorkout] = useState(null);

  useEffect(() => {
    const loadParams = async () => {
      if (!user) return;

      try {
        // Check for in-progress workout first
        const inProgress = await getInProgressWorkouts(user.uid);
        if (inProgress) {
          setInProgressWorkout(inProgress);
        }

        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        const prefs = userDoc.exists() ? userDoc.data().prefs || {} : {};

        const workoutsRef = collection(db, 'users', user.uid, 'workouts');
        const workoutsQuery = query(workoutsRef, orderBy('completedAt', 'desc'), limit(1));
        const workoutsSnap = await getDocs(workoutsQuery);

        let lastTier = 2;

        if (!workoutsSnap.empty) {
          const lastWorkout = workoutsSnap.docs[0].data();
          lastTier = lastWorkout.tier ?? 2;
        }

        const duration = prefs.defaultDurationMin || 30;

        setTier(lastTier);
        setDurationMin(duration);
      } catch (error) {
        console.error('Error loading params:', error);
      } finally {
        setLoading(false);
      }
    };

    loadParams();
  }, [user]);

  const handleAdhocStart = (focus) => {
    const params = new URLSearchParams({
      tier: tier,
      minutes: durationMin,
      focus: focus === 'random' ? ADHOC_OPTIONS[Math.floor(Math.random() * ADHOC_OPTIONS.length)].focus : focus,
      quickStart: 'true',
    });
    navigate(`/session/quick/overview?${params.toString()}`);
  };

  const handlePlanWeek = () => {
    navigate('/setup');
  };

  const handleResumeWorkout = () => {
    if (inProgressWorkout) {
      navigate(`/session/quick?resume=${inProgressWorkout.id}`);
    }
  };

  const handleDiscardWorkout = async () => {
    if (!user || !inProgressWorkout) return;

    if (window.confirm('Discard this in-progress workout? This cannot be undone.')) {
      try {
        await deleteInProgressWorkout(user.uid, inProgressWorkout.id);
        localStorage.removeItem('gymbo_active_workout');
        localStorage.removeItem(`gymbo_workout_state_${inProgressWorkout.id}`);
        setInProgressWorkout(null);
        toast.success('Workout discarded');
      } catch (error) {
        console.error('Error discarding workout:', error);
        toast.error('Failed to discard workout');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="text-lg font-plex text-light-ink dark:text-dark-ink">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg px-4 py-6">
      <div className="bg-light-surface dark:bg-dark-surface rounded-[20px] p-6 space-y-6">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <Dumbbell size={48} className="text-light-accent dark:text-dark-accent" />
          </div>
          <h1 className="text-[30px] font-grotesk font-bold text-light-ink dark:text-dark-ink tracking-[-0.02em] mb-2">
            Gymbo
          </h1>
          <p className="text-[15px] font-plex text-light-muted dark:text-dark-muted">
            Your workout trainer
          </p>
        </div>

        <div className="space-y-4">
          {inProgressWorkout && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-[15px] font-plex font-semibold text-light-ink dark:text-dark-ink">
                  In Progress
                </h2>
                <button
                  onClick={handleDiscardWorkout}
                  className="w-8 h-8 flex items-center justify-center rounded-[10px] hover:bg-light-border dark:hover:bg-dark-border transition-colors"
                  title="Discard workout"
                >
                  <X size={18} className="text-light-muted dark:text-dark-muted" />
                </button>
              </div>
              <button
                onClick={handleResumeWorkout}
                className="w-full h-[68px] bg-light-accent dark:bg-dark-accent text-white rounded-[17px] font-plex font-semibold text-[17px] hover:bg-light-accent-press dark:hover:bg-dark-accent transition-colors flex items-center justify-center gap-3 shadow-lg"
              >
                <Play size={24} />
                <div className="text-left">
                  <div className="text-[17px] font-bold">Resume Workout</div>
                  <div className="text-[13px] opacity-90 font-normal capitalize">
                    {inProgressWorkout.focus.replace('-', ' ')} · {inProgressWorkout.exercises?.length || 0} exercises
                  </div>
                </div>
              </button>
            </div>
          )}

          <div>
            <h2 className="text-[15px] font-plex font-semibold text-light-ink dark:text-dark-ink mb-3">
              Quick Start
            </h2>
            <div className="grid grid-cols-2 gap-2">
              {ADHOC_OPTIONS.map((option) => (
                <button
                  key={option.focus}
                  onClick={() => handleAdhocStart(option.focus)}
                  className="h-[58px] bg-light-accent dark:bg-dark-accent text-white rounded-[14px] font-plex font-semibold text-[15px] hover:bg-light-accent-press dark:hover:bg-dark-accent transition-colors flex items-center justify-center gap-2"
                >
                  <span>{option.emoji}</span>
                  <span>{option.label}</span>
                </button>
              ))}
              <button
                onClick={() => handleAdhocStart('random')}
                className="h-[58px] col-span-2 bg-light-surface dark:bg-dark-surface border-2 border-light-accent dark:border-dark-accent text-light-accent dark:text-dark-accent rounded-[14px] font-plex font-semibold text-[15px] hover:bg-light-accent-soft dark:hover:bg-dark-accent-soft transition-colors flex items-center justify-center gap-2"
              >
                <Shuffle size={18} />
                <span>Random</span>
              </button>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-light-border dark:border-dark-border"></div>
            </div>
            <div className="relative flex justify-center text-[13px]">
              <span className="px-2 bg-light-surface dark:bg-dark-surface text-light-muted dark:text-dark-muted font-plex">
                or
              </span>
            </div>
          </div>

          <button
            onClick={handlePlanWeek}
            className="w-full h-[58px] border-2 border-light-border dark:border-dark-border text-light-ink dark:text-dark-ink rounded-[17px] font-plex font-semibold text-[15px] hover:border-light-ink dark:hover:border-dark-accent transition-colors"
          >
            Plan a full week
          </button>
        </div>
      </div>
    </div>
  );
}

export default Home;
